// Kleine Bausteine für alles rund ums Senden: Zustandsanzeigen, kopierbare Werte.

import { useState, type ReactNode } from "react";
import type { EngineStateName, LiveIngest, SrtIngest } from "../types";

type Tone = "ok" | "warn" | "error" | "idle";

/** Zustandspunkt mit weichem Hof – liest sich auch in dunkler Umgebung. */
export function StatusDot({ tone }: { tone: Tone }): JSX.Element {
  const color = {
    ok: "bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.18)]",
    warn: "bg-amber-500 shadow-[0_0_0_3px_rgba(245,158,11,0.2)]",
    error: "bg-red-500 shadow-[0_0_0_3px_rgba(239,68,68,0.2)]",
    idle: "bg-[var(--text-muted)] opacity-60",
  }[tone];
  return <span className={`inline-block h-2 w-2 flex-shrink-0 rounded-full ${color}`} aria-hidden="true" />;
}

/** Zustand als Etikett im Badge-Stil (Punkt + Wort). */
export function StatusBadge({ tone, children, title, live = false }: { tone: Tone; children: ReactNode; title?: string; live?: boolean }): JSX.Element {
  const cls = { ok: "badge-success", warn: "badge-warning", error: "badge-danger", idle: "badge-neutral" }[tone];
  return (
    <span className={`badge ${cls} whitespace-nowrap`} title={title}>
      {live ? (
        <span className="inline-block h-1.5 w-1.5 flex-shrink-0 animate-live-pulse rounded-full bg-red-500" aria-hidden="true" />
      ) : (
        <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-current" aria-hidden="true" />
      )}
      {children}
    </span>
  );
}

const ENGINE_LABEL: Record<EngineStateName, { label: string; tone: Tone; hint: string }> = {
  running: { label: "Sendet", tone: "ok", hint: "Der Kanal läuft in der Sende-Engine." },
  check_failed: { label: "Skript fehlerhaft", tone: "error", hint: "Liquidsoap hat das erzeugte Skript abgelehnt; der Kanal sendet nicht." },
  check_failed_running_previous: { label: "Änderung abgelehnt", tone: "warn", hint: "Die letzte Änderung war fehlerhaft; es läuft weiter der vorige Stand." },
  start_failed: { label: "Startet nicht", tone: "error", hint: "Der Kanal bricht direkt nach dem Start ab; die Engine versucht es erneut." },
  crashed: { label: "Abgestürzt", tone: "error", hint: "Der Kanal ist ausgefallen; die Engine startet ihn neu." },
  not_deployed: { label: "Engine meldet nichts", tone: "idle", hint: "Die Sende-Engine hat diesen Kanal (noch) nicht übernommen. Läuft der Container liquidsoap?" },
};

/** Zustand eines Kanals in der Sende-Engine. „Sendet“ bekommt den pulsierenden On-Air-Punkt. */
export function EngineBadge({ state, active }: { state: EngineStateName | null; active: boolean }): JSX.Element {
  if (!active) {
    return (
      <span title="Nur aktive Kanäle senden.">
        <StatusBadge tone="idle">Nicht aktiv</StatusBadge>
      </span>
    );
  }
  const info = ENGINE_LABEL[state ?? "not_deployed"];
  return (
    <StatusBadge tone={info.tone} title={info.hint} live={state === "running"}>
      {info.label}
    </StatusBadge>
  );
}

export function engineHint(state: EngineStateName): string {
  return ENGINE_LABEL[state].hint;
}

/** Kleine Symbole für den Sendebetrieb (Linienstil, erben die Textfarbe). */
export function Icon({ name, className = "h-4 w-4" }: {
  name: "copy" | "check" | "eye" | "eye-off" | "refresh" | "users" | "skip" | "log" | "alert" | "x" | "plus" | "up" | "down" | "trash" | "grip" | "server" | "clock" | "music" | "chevron";
  className?: string;
}): JSX.Element {
  const paths: Record<string, JSX.Element> = {
    copy: <><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h8" /></>,
    check: <path d="m5 12 5 5 9-10" />,
    eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></>,
    "eye-off": <><path d="M3 3l18 18" /><path d="M10.6 5.1A10.4 10.4 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.2 4.2M6.6 6.6A17 17 0 0 0 2 12s3.5 7 10 7a9.7 9.7 0 0 0 5.4-1.6" /></>,
    refresh: <><path d="M20 11a8 8 0 0 0-14.8-4M4 4v4h4" /><path d="M4 13a8 8 0 0 0 14.8 4M20 20v-4h-4" /></>,
    users: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><path d="M16 4.5a3.5 3.5 0 0 1 0 7M18 14.5a6.5 6.5 0 0 1 3.5 5.5" /></>,
    skip: <><path d="M5 5l10 7-10 7V5Z" /><path d="M19 5v14" /></>,
    log: <><path d="M4 6h16M4 12h16M4 18h10" /></>,
    alert: <><path d="M12 3 2 20h20L12 3Z" /><path d="M12 10v4M12 17h.01" /></>,
    x: <path d="M6 6l12 12M18 6 6 18" />,
    plus: <path d="M12 5v14M5 12h14" />,
    up: <path d="m6 15 6-6 6 6" />,
    down: <path d="m6 9 6 6 6-6" />,
    trash: <><path d="M4 7h16M10 11v6M14 11v6" /><path d="M6 7l1 13h10l1-13M9 7V4h6v3" /></>,
    grip: <><circle cx="9" cy="6" r="1" /><circle cx="15" cy="6" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="9" cy="18" r="1" /><circle cx="15" cy="18" r="1" /></>,
    server: <><rect x="3" y="4" width="18" height="7" rx="2" /><rect x="3" y="13" width="18" height="7" rx="2" /><path d="M7 7.5h.01M7 16.5h.01" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    music: <><path d="M9 18V5l11-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="17" cy="16" r="3" /></>,
    chevron: <path d="m9 6 6 6-6 6" />,
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={`flex-shrink-0 ${className}`} aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

/** Beschrifteter Wert zum Kopieren – so, wie ihn BUTT & Co. abfragen. */
export function CopyValue({ value, secret = false, label }: { value: string; secret?: boolean; label: string }): JSX.Element {
  const [shown, setShown] = useState(!secret);
  const [copied, setCopied] = useState(false);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setShown(true);
    }
  };
  return (
    <div className="group flex min-w-0 items-center gap-3">
      <span className="w-20 flex-shrink-0 text-xs font-medium text-muted">{label}</span>
      <div className="flex h-8 min-w-0 flex-1 items-center rounded-lg border border-default bg-input pl-2.5 pr-1">
        <code className="min-w-0 flex-1 truncate font-mono text-[0.78rem] text-primary">
          {shown ? value : "•".repeat(Math.min(value.length, 16))}
        </code>
        {secret && (
          <button
            type="button"
            onClick={() => setShown((s) => !s)}
            className="flex h-6 w-6 items-center justify-center rounded-md text-muted hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"
            title={shown ? "Verbergen" : "Zeigen"}
            aria-label={shown ? "Verbergen" : "Zeigen"}
          >
            <Icon name={shown ? "eye-off" : "eye"} className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={copy}
          className={`flex h-6 items-center gap-1 rounded-md px-1.5 text-[0.7rem] font-medium ${copied ? "text-emerald-600 dark:text-emerald-400" : "text-muted hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"}`}
          title={copied ? "Kopiert" : "Kopieren"}
        >
          <Icon name={copied ? "check" : "copy"} className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{copied ? "Kopiert" : "Kopieren"}</span>
        </button>
      </div>
    </div>
  );
}

/** Zugangsdaten für den Live-Eingang, so beschriftet wie in BUTT, Mixxx und SAM Broadcaster. */
export function IngestCard({ ingest, onRenew }: { ingest: LiveIngest; onRenew?: () => void }): JSX.Element {
  return (
    <div className="space-y-2">
      <CopyValue label="Server" value={ingest.host} />
      <CopyValue label="Port" value={String(ingest.port)} />
      <CopyValue label="Mount" value={ingest.mount} />
      <CopyValue label="Benutzer" value={ingest.user} />
      <CopyValue label="Passwort" value={ingest.password} secret />
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <span className="min-w-0 flex-1 text-xs leading-relaxed text-muted">
          Typ „Icecast 2" wählen. Sobald jemand verbunden ist, übernimmt der Live-Eingang; danach läuft die Automatik weiter.
        </span>
        {onRenew && (
          <button type="button" onClick={onRenew} className="btn btn-ghost btn-sm">
            <Icon name="refresh" className="h-3.5 w-3.5" /> Neues Passwort
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * SRT-Eingang: für OBS, Handy-Apps (Larix Broadcaster) und Hardware-Encoder. Anders als der
 * Live-Eingang über UDP und mit Verschlüsselung; übersteht wacklige Leitungen besser.
 */
export function SrtCard({ srt, connected, onToggle, onRenew }: {
  srt: SrtIngest;
  /** Gerade eine SRT-Verbindung da? (null = unbekannt, z. B. ältere Engine) */
  connected: boolean | null;
  onToggle?: (enabled: boolean) => void;
  onRenew?: () => void;
}): JSX.Element {
  const [help, setHelp] = useState(false);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <label className={`inline-flex items-center gap-2 text-sm font-medium text-primary ${onToggle ? "cursor-pointer" : ""}`}>
          <input type="checkbox" checked={srt.enabled} disabled={!onToggle} onChange={(e) => onToggle?.(e.target.checked)} />
          SRT-Eingang {srt.enabled ? "an" : "aus"}
        </label>
        {srt.enabled && connected !== null && (
          <StatusBadge tone={connected ? "ok" : "idle"}>{connected ? "verbunden" : "wartet auf Verbindung"}</StatusBadge>
        )}
      </div>
      {srt.enabled ? (
        <>
          <CopyValue label="Adresse" value={srt.url} secret />
          <CopyValue label="Server" value={srt.host} />
          <CopyValue label="Port (UDP)" value={String(srt.port)} />
          <CopyValue label="Passphrase" value={srt.passphrase} secret />
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <button type="button" onClick={() => setHelp((h) => !h)} className="text-xs font-medium text-brand-primary hover:underline">
              {help ? "Anleitung ausblenden" : "So geht's mit OBS und Larix"}
            </button>
            {onRenew && (
              <button type="button" onClick={onRenew} className="btn btn-ghost btn-sm">
                <Icon name="refresh" className="h-3.5 w-3.5" /> Neue Passphrase
              </button>
            )}
          </div>
          {help && (
            <div className="well space-y-2 text-xs leading-relaxed text-secondary">
              <p>
                <b>OBS Studio:</b> Einstellungen → Stream → Dienst „Benutzerdefiniert“. Bei „Server“ die ganze
                Adresse oben einfügen, „Streamschlüssel“ leer lassen. Unter Ausgabe den Audio-Encoder auf AAC
                (128–192 kbit/s). Bild darf mitgesendet werden, das Radio nimmt nur den Ton.
              </p>
              <p>
                <b>Larix Broadcaster (Handy):</b> Einstellungen → Verbindungen → Neu. URL: die Adresse oben ohne
                „?mode=…&amp;passphrase=…“, Modus „Caller“, Passphrase eintragen. Für reinen Ton unter Video
                „Nur Audio“ wählen.
              </p>
              <p>
                <b>Wichtig:</b> Ist gleichzeitig das Studio live, hat das Studio Vorrang. Verstummt SRT länger als
                der Stille-Wächter erlaubt, übernimmt die Automatik.
              </p>
            </div>
          )}
        </>
      ) : (
        <p className="text-xs text-muted">
          Für OBS, Handy-Apps und Außenübertragungen. Ausgeschaltet nimmt der Kanal keine SRT-Verbindung an.
        </p>
      )}
    </div>
  );
}

export function randomPassword(length = 24): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
}

export function errorText(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  const body = raw.replace(/^\d{3}\s*/, "");
  try {
    const parsed = JSON.parse(body) as { error?: string; message?: string; issues?: Array<{ path: string[]; message: string }> };
    if (parsed.issues?.length) return parsed.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join("; ");
    const known: Record<string, string> = {
      forbidden: "Dafür fehlen die Rechte.",
      managed_server_address_fixed: "Adresse und Port der Suite-Server legt docker-compose fest.",
      server_in_use: "Der Server wird noch von Kanälen genutzt.",
      host_not_allowed: parsed.message ?? "Dieser Host ist nicht erlaubt.",
      mount_taken: "Dieser Mount ist auf dem Server schon vergeben.",
      sid_taken: "Diese Stream-Nummer ist auf dem Server schon vergeben.",
      engine_unavailable: "Die Sende-Engine ist nicht erreichbar.",
      engine_ports_exhausted: "Alle Engine-Ports sind belegt.",
    };
    return (parsed.error && known[parsed.error]) || parsed.message || parsed.error || raw;
  } catch {
    return raw;
  }
}
